import { PlannerPoi, POI, PoiOption } from 'components/common/types/Planner.types';
import { parseCSVFile, parseExcelFile } from 'lib/FileUpload/fileParser';
import { CustomPoiFile, CustomPoiLocation } from '../PointsOfInterest.types';
import { CUSTOM_POI_DATA_PROVIDER, CUSTOM_POI_MAX_ROWS, POI_DEFAULT_RADIUS } from '../consts';

const CSV_EXTENSIONS = ['.csv'];
const EXCEL_EXTENSIONS = ['.xlsx', '.xls'];

const LATITUDE_COLUMN_NAMES = ['latitude', 'lat'];
const LONGITUDE_COLUMN_NAMES = ['longitude', 'long', 'lng', 'lon'];
const NAME_COLUMN_NAMES = [
  'name',
  'poi name',
  'poi_name',
  'category name',
  'category_name',
  'category type',
  'category_type',
];

const isValidLatitude = (value: number): boolean => value >= -90 && value <= 90;
const isValidLongitude = (value: number): boolean => value >= -180 && value <= 180;

const findColumnIndex = (headers: string[], candidates: string[]): number =>
  headers.findIndex((header) => candidates.includes(header.toLowerCase().trim()));

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : '';
};

interface ParsedRow {
  name: string;
  latitude: number;
  longitude: number;
}

const parseCSVRows = async (file: File): Promise<ParsedRow[]> => {
  const data = await parseCSVFile(file);
  if (data.length < 2) return [];

  const headers = data[0];
  const latitudeIndex = findColumnIndex(headers, LATITUDE_COLUMN_NAMES);
  const longitudeIndex = findColumnIndex(headers, LONGITUDE_COLUMN_NAMES);
  const nameIndex = findColumnIndex(headers, NAME_COLUMN_NAMES);

  if (latitudeIndex === -1 || longitudeIndex === -1) {
    throw new Error('File must contain latitude and longitude columns.');
  }

  return data.slice(1).map((row) => ({
    name: nameIndex >= 0 ? (row[nameIndex] ?? '').trim() : '',
    latitude: parseFloat(row[latitudeIndex]),
    longitude: parseFloat(row[longitudeIndex]),
  }));
};

const parseExcelRows = async (file: File): Promise<ParsedRow[]> => {
  const data = await parseExcelFile(file);
  if (data.length === 0) return [];

  const headers = [...new Set(data.flatMap(Object.keys))];
  const latitudeKey = headers.find((header) => LATITUDE_COLUMN_NAMES.includes(header.toLowerCase().trim()));
  const longitudeKey = headers.find((header) => LONGITUDE_COLUMN_NAMES.includes(header.toLowerCase().trim()));
  const nameKey = headers.find((header) => NAME_COLUMN_NAMES.includes(header.toLowerCase().trim()));

  if (!latitudeKey || !longitudeKey) {
    throw new Error('File must contain latitude and longitude columns.');
  }

  return data.map((row) => ({
    name: nameKey ? String(row[nameKey] ?? '').trim() : '',
    latitude: parseFloat(String(row[latitudeKey])),
    longitude: parseFloat(String(row[longitudeKey])),
  }));
};

export const parseFileRows = async (file: File): Promise<ParsedRow[]> => {
  const extension = getFileExtension(file.name);

  if (CSV_EXTENSIONS.includes(extension)) {
    return parseCSVRows(file);
  }

  if (EXCEL_EXTENSIONS.includes(extension)) {
    return parseExcelRows(file);
  }

  throw new Error('File must be CSV, XLSX, or XLS.');
};

const formatInvalidCoordinatesError = (rows: number[]): string => {
  const displayed = rows.slice(0, 5).join(', ');
  const suffix = rows.length > 5 ? '...' : '';
  return `Invalid coordinates found at row(s): ${displayed}${suffix}.`;
};

export const validateParsedRows = (
  rows: ParsedRow[],
  customPoiMaxRows = CUSTOM_POI_MAX_ROWS,
): { isValid: boolean; error?: string; values: CustomPoiLocation[] } => {
  if (rows.length === 0) {
    return { isValid: false, error: 'File contains no data rows.', values: [] };
  }

  if (rows.length > customPoiMaxRows) {
    return { isValid: false, error: `File upload cannot exceed ${customPoiMaxRows} rows.`, values: [] };
  }

  const values: CustomPoiLocation[] = [];
  const invalidCoordinates: number[] = [];

  rows.forEach((row, index) => {
    const { latitude, longitude, name } = row;

    if (
      Number.isNaN(latitude) ||
      Number.isNaN(longitude) ||
      !isValidLatitude(latitude) ||
      !isValidLongitude(longitude)
    ) {
      invalidCoordinates.push(index + 2);
      return;
    }

    values.push({
      name: name || `${latitude}, ${longitude}`,
      latitude,
      longitude,
      radius: POI_DEFAULT_RADIUS,
    });
  });

  if (invalidCoordinates.length) {
    return {
      isValid: false,
      error: formatInvalidCoordinatesError(invalidCoordinates),
      values: [],
    };
  }

  return { isValid: true, values };
};

export const transformToPoiOptions = (fileName: string, values: CustomPoiLocation[]): PoiOption => {
  const categoryPath = `${CUSTOM_POI_DATA_PROVIDER}/${fileName}`;

  return {
    code: categoryPath,
    name: fileName,
    selectable: true,
    categoryType: fileName,
    categoryPath,
    children: values.map((value, index) => ({
      code: `${categoryPath}/${value.name}-${index}`,
      name: value.name,
      selectable: true,
      categoryType: fileName,
      categoryPath: `${categoryPath}/${value.name}-${index}`,
      parentCode: fileName,
      latitude: value.latitude,
      longitude: value.longitude,
      radius: value.radius,
    })),
    include: true,
  };
};

export const buildPoiPoint = (
  poi: POI,
  value: { name: string; latitude: number; longitude: number },
  id: string,
): PlannerPoi => ({
  id,
  name: value.name,
  geometry: { coordinates: [value.longitude, value.latitude] as [number, number] },
  properties: {
    categoryName: poi.parentCategory || poi.name,
    categoryType: poi.code,
    address: '',
    postalCode: '',
    dataProvider: CUSTOM_POI_DATA_PROVIDER,
    mediaOwner: '',
    type: 'custom',
    visibility: { include: poi.include, radius: poi.radius },
  },
});

export const enrichCustomPOIs = (pois: POI[], customPoiFiles: CustomPoiFile[]): POI[] =>
  pois.map((poi) => {
    if (poi.dataProvider !== CUSTOM_POI_DATA_PROVIDER) return poi;

    const file = customPoiFiles.find(
      (customFile) => customFile.fileName === poi.parentCategory || customFile.fileName === poi.code,
    );
    if (!file) return poi;

    if (poi.hasChildren) {
      return {
        ...poi,
        poiPoints: file.values.map((value, index) => buildPoiPoint(poi, value, `custom-${poi.categoryPath}-${index}`)),
      };
    }

    const matchingValue = file.values.find((value, index) => {
      const expectedPath = `${CUSTOM_POI_DATA_PROVIDER}/${file.fileName}/${value.name}-${index}`;
      return expectedPath === poi.categoryPath;
    });
    if (!matchingValue) return poi;

    return {
      ...poi,
      poiPoints: [buildPoiPoint(poi, matchingValue, `custom-${poi.categoryPath}`)],
    };
  });

export const restoreCustomPoiState = (pois: POI[]): { files: CustomPoiFile[]; categories: PoiOption[] } => {
  const customPois = pois.filter((poi) => poi.dataProvider === CUSTOM_POI_DATA_PROVIDER);

  if (customPois.length === 0) return { files: [], categories: [] };

  const fileMap = new Map<string, CustomPoiLocation[]>();

  customPois.forEach((poi) => {
    if (poi.hasChildren) {
      const values = (poi.poiPoints ?? []).map((point) => ({
        name: point.name,
        latitude: point.geometry.coordinates[1],
        longitude: point.geometry.coordinates[0],
        radius: poi.radius,
      }));

      fileMap.set(poi.code, values);
    } else {
      const point = poi.poiPoints?.[0];

      if (!point) return;

      const existing = fileMap.get(poi.code) ?? [];

      existing.push({
        name: poi.name,
        latitude: point.geometry.coordinates[1],
        longitude: point.geometry.coordinates[0],
        radius: poi.radius,
      });

      fileMap.set(poi.code, existing);
    }
  });

  const files: CustomPoiFile[] = Array.from(fileMap.entries()).map(([fileName, values]) => ({
    fileName,
    values,
  }));

  const categories = files.map((file) => transformToPoiOptions(file.fileName, file.values));

  return { files, categories };
};
